import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { useTaskSearch } from './useTaskSearch';
import { TaskCard } from '@/features/tasks/TaskCard';
import { Skeleton, SkeletonGroup } from '@/components/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import type { Task } from '@/types/api';

/**
 * Search as a panel over the board.
 *
 * It opens near the top, roughly where the trigger sits, so the thing you clicked and
 * the thing that appears are in the same place. The board dims behind rather than
 * being replaced — search is a question asked FROM somewhere, and replacing the board
 * costs your scroll position and your filters every time you look something up.
 *
 * Native <dialog>. Focus trapping, Escape, inertness of the page behind, and the top
 * layer come free and correct; hand-rolled versions of those are where modal
 * accessibility usually goes wrong.
 *
 * No arrow-key navigation and no shortcut hints. Results are cards you click, and a
 * row of keyboard legends is a whole extra thing to read for a list of three.
 */
export function SearchOverlay({
  open,
  onClose,
  onOpenTask,
  assigneeId,
  showAssignee,
}: {
  open: boolean;
  onClose: () => void;
  onOpenTask: (task: Task) => void;
  /** Narrows results the same way the board behind this panel is narrowed. */
  assigneeId?: string | undefined;
  showAssignee: boolean;
}) {
  const queryClient = useQueryClient();
  const ref = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState('');
  const search = useTaskSearch(term, assigneeId);
  const items = search.data?.items ?? [];

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
      inputRef.current?.focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    /*
      Fires for Escape as well as close(), so every path resets the same way.

      The cached results are REMOVED, not just the term. Search results are a snapshot
      of a question you already asked — keeping them means the panel reopens showing
      the last thing you looked for, which is a small privacy leak on a shared screen
      and confusing on your own.
    */
    const handle = () => {
      setTerm('');
      queryClient.removeQueries({ queryKey: ['taskSearch'] });
      onClose();
    };
    dialog.addEventListener('close', handle);
    return () => dialog.removeEventListener('close', handle);
  }, [onClose, queryClient]);

  return (
    <dialog
      ref={ref}
      aria-label="Search tasks"
      onClick={(event) => {
        // The backdrop IS the dialog element, so this only fires on a miss.
        if (event.target === ref.current) ref.current?.close();
      }}
      className="palette w-full max-w-full border-0 bg-transparent p-0"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        /*
          A FIXED height, not max-height.

          The panel used to be as tall as its contents, so it grew as you typed and
          shrank as results narrowed — the box jumping around underneath a cursor that
          is trying to read it. A constant size means only the list inside moves.
        */
        className="mx-auto flex h-[min(36rem,calc(100dvh-7rem))] w-[min(38rem,92vw)] flex-col overflow-hidden rounded-2xl border p-3"
        style={{
          borderColor: 'var(--border)',
          background: 'var(--surface)',
          boxShadow: 'var(--shadow-panel)',
        }}
      >
        <div className="relative shrink-0">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-faint)' }}
          />
          <input
            ref={inputRef}
            type="text"
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="Search a title, or a reference like S-142"
            aria-label="Search tasks"
            className="min-h-11 w-full rounded-xl border pl-9 pr-9 text-sm outline-none"
            style={{
              borderColor: 'var(--border)',
              background: 'var(--surface-sunken)',
              color: 'var(--text)',
            }}
          />
          {term && (
            <button
              type="button"
              onClick={() => {
                setTerm('');
                inputRef.current?.focus();
              }}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded"
              style={{ color: 'var(--text-muted)' }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-y-auto no-scrollbar">
          {!search.active && (
            <p
              className="flex flex-1 items-center justify-center px-6 text-center text-xs"
              style={{ color: 'var(--text-faint)' }}
            >
              Type at least two characters — a title, or a reference like S-142.
            </p>
          )}

          {search.active && search.isFetching && !search.data && (
            <SkeletonGroup label="Searching" className="flex flex-col gap-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height={88} radius={12} />
              ))}
            </SkeletonGroup>
          )}

          {search.active && search.error && (
            <p
              className="flex flex-1 items-center justify-center px-6 text-center text-xs"
              style={{ color: 'var(--text-muted)' }}
            >
              {search.error.message}
            </p>
          )}

          {search.active && search.data && items.length === 0 && (
            <EmptyState
              size="sm"
              title={`Nothing matches “${search.query}”.`}
              description="Try fewer words, or the card reference — S-142."
            />
          )}

          {/* `search.active` as well as a non-empty list: without it, anything left in
              the cache renders underneath the "type at least two characters" prompt. */}
          {search.active && items.length > 0 && (
            <div className="flex flex-col gap-2">
              {items.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  showAssignee={showAssignee}
                  highlight={search.query}
                  onOpen={(t) => {
                    onOpenTask(t);
                    ref.current?.close();
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
}
